// Copyright (c) Ping. All rights reserved.
//
// App-wide call host. Mounted once in AppShell. Responsibilities:
//   • install the global call-signaling feed (client.onMessage → call store)
//   • render incoming ringing banners (Figma 2444:7654) for any ringing
//     session, plus play/stop the ringtone
//   • render the active-call surface (ActiveCallView) + draggable minimized card
// Launch points (chat header / call history) call useCallStore().startCall.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View, type ViewStyle } from 'react-native';

import { MonitorSmartphone } from 'lucide-react-native';

import { type CallSignalSession,IncomingCallBanner } from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { RingtoneManager } from '@calls/notifications/RingtoneManager';
import { clearReloadAttempt, markReloadAttempt } from '@calls/reloadGuard';
import { installCallSignalingFeed } from '@calls/signalingFeed';
import { useCallStore } from '@calls/store/callStore';
import { ActiveCallView } from '@calls/ui/ActiveCallView';
import { ringback } from '@calls/ui/ringback';
import { toastStore } from '@features/toasts/toastStore';

export interface CallHostProps {
  /** Open a conversation (for "Reply in message" on a declined call). */
  readonly onOpenConversation?: (conversationId: string) => void;
}

export function CallHost({ onOpenConversation }: CallHostProps): JSX.Element {
  const authed = useSessionStore((s) => s.status.kind === 'authenticated');
  const rings = useCallStore((s) => s.rings);
  const activeCallId = useCallStore((s) => s.active?.callId ?? null);
  const handoffPrompt = useCallStore((s) => s.handoffPrompt);
  const ringtoneRef = useRef<RingtoneManager | null>(null);
  // "Remind me later" timers can be scheduled up to an hour out — track them so
  // host unmount (sign-out) cancels them instead of firing a toast / starting a
  // call for a logged-out user.
  const remindTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // (Re)install the signaling feed whenever the session authenticates.
  useEffect(() => {
    if (!authed) return;
    return installCallSignalingFeed();
  }, [authed]);

  // Incoming = ringing sessions that aren't the call we're already in.
  const incoming = useMemo(
    () =>
      Array.from(rings.values())
        .filter((r: CallSignalSession) => r.status === 'ringing' && r.callId !== activeCallId)
        // Never show two incoming banners — surface only the most recent ring
        // (others are stale/expired and self-clear on end/decline/timeout).
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 1),
    [rings, activeCallId],
  );

  // Ringtone: play while at least one call is ringing and we're not in a call.
  useEffect(() => {
    if (ringtoneRef.current === null) ringtoneRef.current = new RingtoneManager({ loop: true });
    const mgr = ringtoneRef.current;
    if (incoming.length > 0 && activeCallId === null) mgr.play();
    else mgr.stop();
    return () => mgr.stop();
  }, [incoming.length, activeCallId]);

  // Stop + dispose the ringtone AND cancel any pending "remind me" timers when
  // the host unmounts (sign-out) — no dangling long timers / post-teardown work.
  useEffect(
    () => () => {
      ringtoneRef.current?.destroy();
      for (const id of remindTimersRef.current) clearTimeout(id);
      remindTimersRef.current.clear();
    },
    [],
  );

  // Browser close / tab hide while in a call → leave gracefully. `pagehide`
  // (not `beforeunload`) is the reliable, bfcache-friendly teardown signal. We
  // disconnect LiveKit immediately so peers see us drop within seconds (their
  // onParticipants fires → 1-1 ends, group removes us), and fire a best-effort
  // `endCall` so backend history + (when we're the last) the call_end land. The
  // MLS send may not flush on an abrupt close — peers' own LiveKit
  // participant-disconnect handling is the backstop. Idempotent (endCall no-ops
  // when there's no active call).
  useEffect(() => {
    const onHide = (): void => {
      if (useCallStore.getState().active !== null) {
        void useCallStore.getState().endCall('left');
      }
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Active-call reload guard. The browser's native "Reload site?" dialog CANNOT
  // be customised (its text is browser-controlled) and suspends the tab (which
  // can drop LiveKit), so for the common keyboard reload (Cmd/Ctrl+R) we INTERCEPT
  // it and show our OWN modal with clear copy and correct behaviour: Cancel keeps
  // the call fully alive (we never start an unload), Leave ends the call then
  // reloads. The native `beforeunload` stays only as a best-effort fallback for
  // the reload BUTTON / tab close, and stamps `markReloadAttempt()` so a
  // dialog-induced LiveKit disconnect on Cancel doesn't tear the call down
  // (callStore `onState`). `pagehide` (above) still handles a genuine unload.
  const [reloadConfirmOpen, setReloadConfirmOpen] = useState(false);
  useEffect(() => {
    if (activeCallId === null) {
      setReloadConfirmOpen(false);
      return;
    }
    const isReloadCombo = (e: KeyboardEvent): boolean =>
      (e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'r' || e.key === 'R');
    const onKeyDown = (e: KeyboardEvent): void => {
      if (!isReloadCombo(e)) return;
      // Fully swallow the keyboard reload so NO unload sequence starts — the call
      // stays untouched — and surface our own confirm instead.
      e.preventDefault();
      e.stopPropagation();
      setReloadConfirmOpen(true);
    };
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      // Fallback for reload-button / tab-close (not interceptable): trigger the
      // browser's generic confirm and mark the attempt so a transient disconnect
      // while the dialog is up can't kill the call if the user cancels.
      markReloadAttempt();
      e.preventDefault();
      e.returnValue = '';
    };
    // Capture phase so we beat the document-level shortcut handlers.
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [activeCallId]);

  const onStayOnCall = useCallback((): void => {
    clearReloadAttempt();
    setReloadConfirmOpen(false);
  }, []);
  const onLeaveAndReload = useCallback((): void => {
    setReloadConfirmOpen(false);
    // End the call cleanly (LiveKit disconnect + best-effort call_end) THEN
    // reload — so peers see us drop immediately instead of timing us out.
    const finishReload = (): void => {
      try {
        if (typeof window !== 'undefined') window.location.reload();
      } catch {
        /* best-effort */
      }
    };
    const active = useCallStore.getState().active;
    if (active !== null) {
      void useCallStore
        .getState()
        .endCall('left')
        .catch(() => undefined)
        .finally(finishReload);
    } else {
      finishReload();
    }
  }, []);

  // Outgoing ringback: play while our outgoing call is unanswered (no remote
  // in the room yet), stop once someone joins or the call ends.
  const ringbackOn = useCallStore((s) => {
    const a = s.active;
    if (!a || a.direction !== 'outgoing') return false;
    if (s.participants.some((p) => !p.isLocal)) return false;
    return s.phase === 'outgoing' || s.phase === 'connecting' || s.phase === 'connected';
  });
  useEffect(() => {
    if (ringbackOn) ringback.start();
    else ringback.stop();
    return () => ringback.stop();
  }, [ringbackOn]);

  return (
    <>
      {handoffPrompt && activeCallId === null ? (
        <View style={STACK_STYLE} className="gap-[12px]">
          <View className="w-[336px] flex-row items-center gap-[12px] rounded-[16px] border border-[#222429] bg-[#222429] p-[12px]">
            <MonitorSmartphone size={22} color="#537EFF" />
            <View className="flex-1">
              <Text className="text-[15px] font-bold text-[#F5F6F8]">Continue call here?</Text>
              <Text className="text-[13px] text-white">Moved from your other device</Text>
            </View>
            <Pressable
              onPress={() => void useCallStore.getState().acceptHandoff()}
              className="h-[28px] items-center justify-center rounded-[8px] bg-[#3CCB4F] px-[14px] active:opacity-80"
            >
              <Text className="text-[13px] font-bold text-white">Join</Text>
            </Pressable>
            <Pressable
              onPress={() => useCallStore.getState().dismissHandoff()}
              className="h-[28px] items-center justify-center rounded-[8px] bg-white/[0.08] px-[12px] active:opacity-70"
            >
              <Text className="text-[13px] text-white">Dismiss</Text>
            </Pressable>
          </View>
        </View>
      ) : null}

      {incoming.length > 0 ? (
        <View
          style={STACK_STYLE}
          className="gap-[12px]"
        >
          {incoming.map((r) => (
            <IncomingCallBanner
              key={r.callId}
              callerName={r.actorDisplayName ?? 'Incoming call'}
              kind={r.media}
              onAnswer={() => void useCallStore.getState().acceptCall(r.callId)}
              onDecline={() => void useCallStore.getState().declineCall(r.callId)}
              onSwipeDismiss={() => useCallStore.getState().dismissRing(r.callId)}
              onQuickReply={(text) => void useCallStore.getState().quickReply(r.callId, text)}
              onReplyInMessage={() => {
                void useCallStore.getState().declineCall(r.callId);
                onOpenConversation?.(r.conversationId);
              }}
              onRemind={(minutes) => {
                void useCallStore.getState().declineCall(r.callId);
                const name = r.actorDisplayName ?? 'someone';
                toastStore.show(`We'll remind you in ${minutes} min`, { type: 'info' });
                const id = setTimeout(() => {
                  remindTimersRef.current.delete(id);
                  toastStore.show(`Reminder: call ${name} back`, {
                    type: 'info',
                    duration: 'persistent',
                    action: {
                      label: 'Call',
                      onPress: () =>
                        void useCallStore.getState().startCall({
                          conversationId: r.conversationId,
                          media: r.media,
                          title: name,
                          isGroup: false,
                          peerAccountIds: r.initiatorAccount ? [r.initiatorAccount] : [],
                        }),
                    },
                  });
                }, minutes * 60_000);
                remindTimersRef.current.add(id);
              }}
            />
          ))}
        </View>
      ) : null}

      <ActiveCallView />

      {/* Custom active-call reload confirmation (Cmd/Ctrl+R interception). Gives
          the clear copy the native "Reload site?" dialog can't, and — because we
          preventDefault the reload — Cancel leaves the call 100% untouched. */}
      <Modal
        visible={reloadConfirmOpen}
        transparent
        animationType="fade"
        onRequestClose={onStayOnCall}
      >
        <Pressable
          accessibilityRole="none"
          onPress={onStayOnCall}
          style={StyleSheet.absoluteFill}
          className="z-50 flex-1 items-center justify-center px-md bg-black/60"
        >
          <Pressable
            onPress={() => undefined}
            className="w-full max-w-[420px] rounded-[14px] border p-xl gap-md bg-light-surface-primary border-light-border-secondary dark:bg-[#15171C] dark:border-[#22242A] shadow-2xl"
          >
            <Text className="font-sans font-bold text-[18px] leading-6 text-light-text-primary dark:text-white">
              Leave the call?
            </Text>
            <Text className="font-sans text-[14px] leading-5 text-light-text-secondary dark:text-[#B5BAC3]">
              You're on an active call. Reloading the page will disconnect you and
              end the call for you. Do you want to leave?
            </Text>
            <View className="flex flex-row gap-3 pt-sm">
              <Pressable
                accessibilityRole="button"
                onPress={onStayOnCall}
                className="flex-1 h-9 rounded-[8px] items-center justify-center border border-light-border-secondary dark:border-[#2F333B]"
              >
                <Text className="font-sans text-[14px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
                  Stay on call
                </Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                onPress={onLeaveAndReload}
                className="flex-1 h-9 rounded-[8px] items-center justify-center bg-[#E5484D]"
              >
                <Text className="font-sans text-[14px] font-semibold text-white">
                  Leave &amp; reload
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

// fixed positioning + max z-index isn't a NativeWind class on web.
const STACK_STYLE = {
  position: 'fixed',
  top: 16,
  right: 16,
  zIndex: 2147483601,
} as unknown as ViewStyle;
